import React, { useEffect, useState } from 'react';
import { Alert, Grid, ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { useFormik, Form, FormikProvider } from 'formik';
import { DeleteConfirmationDialog, DeleteIcon, EntityEditDialog, SnackDisplay, TextInput } from '@lp/lp-ui';
import { OrganizationSchema } from '../../validationSchemes';
import { OrganizationModel } from '../../typings';
import { useCurrentUser } from '../../app/useCurrentUser';
import identityService from '../../services/IdentityService';
import { getBodyErrors } from '../../services/ApiService';
import OrganizationApplicationsPanel from './OrganizationApplicationsPanel';

type OrganizationFormModel = {
    name: string,
    displayName: string
};

type OrganizationDialogProps = {
    open: boolean,
    organization: OrganizationModel | null, // null => create
    handleClose: (savedMessage?: string, color?: "success" | "error") => void
};

const sameIdSet = (a: number[], b: number[]) =>
    a.length === b.length && a.every((id) => b.includes(id));

const OrganizationDialog: React.FC<OrganizationDialogProps> = ({ open, organization, handleClose }: OrganizationDialogProps) => {
    const [, isTenantAdmin] = useCurrentUser();
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [snackMessage, setSnackMessage] = useState('');
    // Staged organization applications (applied on Save) and the server baseline used to detect changes. null => loading.
    const [organizationApplications, setOrganizationApplications] = useState<number[] | null>(null);
    const [loadedOrganizationApplications, setLoadedOrganizationApplications] = useState<number[] | null>(null);

    const isNew = !organization || organization.id === 0;
    const canEdit = isTenantAdmin;

    const loadOrganizationApplications = async (id: number, isActive: () => boolean = () => true) => {
        try {
            const response = await identityService.getOrganizationApplications(id);
            if (!isActive()) return;
            const ids = response.data.map((a) => a.id);
            setOrganizationApplications(ids);
            setLoadedOrganizationApplications(ids);
        } catch (err: any) {
            if (!isActive()) return;
            // Leave the applications unloaded (panel stays disabled) so a Save can't overwrite the
            // real server list with an empty set that was never actually read.
            const bodyErrors = await getBodyErrors(err);
            setSnackMessage(bodyErrors[0]?.detail ?? 'Failed to load application access.');
        }
    };

    useEffect(() => {
        if (!open) return;
        if (isNew) {
            setOrganizationApplications([]);
            setLoadedOrganizationApplications([]);
            return;
        }
        let active = true;
        loadOrganizationApplications(organization!.id, () => active);
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, organization?.id, isNew]);

    const formik = useFormik<OrganizationFormModel>({
        initialValues: { name: organization?.name ?? '', displayName: organization?.displayName ?? '' },
        enableReinitialize: true,
        validationSchema: OrganizationSchema,
        onSubmit: async (values) => {
            const payload = { name: values.name.trim(), displayName: values.displayName?.trim() ?? '' };
            const stagedIds = organizationApplications ?? [];
            try {
                if (isNew) {
                    const response = await identityService.createOrganization(payload);
                    if (stagedIds.length > 0) {
                        try {
                            await identityService.setOrganizationApplications(response.data.id, stagedIds);
                        } catch {
                            handlePopupClose('Organization created, but applying application access failed. Open the organization to retry.', 'error');
                            return true;
                        }
                    }
                    handlePopupClose('Organization created successfully.');
                    return true;
                }

                await identityService.updateOrganization(organization!.id, payload);
                if (!sameIdSet(stagedIds, loadedOrganizationApplications ?? [])) {
                    try {
                        await identityService.setOrganizationApplications(organization!.id, stagedIds);
                    } catch (err: any) {
                        const bodyErrors = await getBodyErrors(err);
                        setSnackMessage(bodyErrors[0]?.detail ?? 'Failed to update application access.');
                        await loadOrganizationApplications(organization!.id); // resync toggles to the server state
                        return false;
                    }
                }
                handlePopupClose('Organization updated successfully.');
                return true;
            } catch (err: any) {
                const status = err?.status;
                const bodyErrors = await getBodyErrors(err);
                const detail = bodyErrors[0]?.detail ?? 'Failed to save organization.';
                if (status === 409) {
                    formik.setFieldError('name', detail);
                } else {
                    setSnackMessage(detail);
                }
                return false;
            }
        }
    });

    const { errors, touched, getFieldProps, submitForm } = formik;

    const handlePopupClose = (savedMessage?: string, color: "success" | "error" = "success") => {
        formik.resetForm();
        setDeleteOpen(false);
        setOrganizationApplications(null);
        setLoadedOrganizationApplications(null);
        handleClose(savedMessage, color);
    };

    const handleDelete = async () => {
        setDeleteOpen(false);
        try {
            await identityService.deleteOrganization(organization!.id);
            handlePopupClose('Organization deleted successfully.');
        } catch (err: any) {
            const bodyErrors = await getBodyErrors(err);
            setSnackMessage(bodyErrors[0]?.detail ?? 'Failed to delete organization.');
        }
    };

    return (
        <React.Fragment>
            <FormikProvider value={formik}>
                <Form autoComplete="off" noValidate onSubmit={submitForm}>
                    <EntityEditDialog
                        open={open}
                        editing={canEdit}
                        maxWidth="sm"
                        fullWidth
                        title={isNew ? 'New Organization' : 'Organization'}
                        handleClose={() => handlePopupClose()}
                        handleSave={submitForm}
                        showMoreMenu={!isNew && canEdit && !organization?.isProtected}
                        menuItems={[
                            <MenuItem key="organization-delete" onClick={() => setDeleteOpen(true)}>
                                <ListItemIcon>
                                    <DeleteIcon />
                                </ListItemIcon>
                                <ListItemText>
                                    Delete
                                </ListItemText>
                            </MenuItem>
                        ]}
                    >
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Only the Name and Display Name are managed here. All other organization
                            settings must be configured in Auth0.
                        </Alert>
                        <Grid container spacing={1} columnSpacing={6}>
                            <Grid item xs={12}>
                                <TextInput
                                    isEditing={canEdit}
                                    required
                                    type="string"
                                    label="Name"
                                    {...getFieldProps('name')}
                                    error={Boolean(touched.name && errors.name)}
                                    helperText={touched.name && errors.name}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextInput
                                    isEditing={canEdit}
                                    required
                                    type="string"
                                    label="Display Name"
                                    {...getFieldProps('displayName')}
                                    error={Boolean(touched.displayName && errors.displayName)}
                                    helperText={touched.displayName && errors.displayName}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <OrganizationApplicationsPanel value={organizationApplications} onChange={setOrganizationApplications} />
                            </Grid>
                        </Grid>
                    </EntityEditDialog>
                </Form>
            </FormikProvider>

            <DeleteConfirmationDialog
                title="Are you sure you'd like to delete this organization?"
                open={deleteOpen}
                handleClose={() => setDeleteOpen(false)}
                handleSave={handleDelete}
            />

            {snackMessage && (
                <SnackDisplay
                    snack={snackMessage}
                    color="error"
                    handleClose={() => setSnackMessage('')}
                />
            )}
        </React.Fragment>
    );
};

export default OrganizationDialog;
